import { useMemo } from "react";
import { useStoreSettings } from "../contexts/StoreSettingsContext";

/**
 * TopInfoBar
 *
 * Slim utility bar shown above the main navigation on every page.
 * Surfaces the most-asked information for restaurant visitors:
 *  - Phone number (tap-to-call on mobile)
 *  - Opening hours (placeholder until real hours are configured)
 *  - Address (tap to open in maps)
 *
 * Designed mobile-first, fully responsive, WCAG 2.1 AA compliant
 * (semantic landmarks, accessible labels, 44px touch targets on mobile).
 */
const TopInfoBar = () => {
  const { settings } = useStoreSettings();

  const phone = useMemo(
    () => settings?.contact_phone || "01726-367742",
    [settings?.contact_phone],
  );
  const phoneHref = useMemo(
    () => `tel:${phone.replace(/[^0-9+]/g, "")}`,
    [phone],
  );
  const address = useMemo(() => {
    const settingsAddress = (settings as unknown as { address?: string } | null)
      ?.address;
    return settingsAddress || "Shuvash Chandra Road, Chitrar Mor, Jessore";
  }, [settings]);
  const mapsHref = useMemo(
    () =>
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
    [address],
  );
  // Placeholder hours — easy to swap once real hours are confirmed
  const hours = "Daily · 11:00 AM – 11:00 PM";

  return (
    <div
      className="w-full border-b text-xs"
      style={{
        backgroundColor: "rgba(var(--bg-dark-rgb), 0.92)",
        borderColor: "rgba(var(--text-main-rgb), 0.08)",
        color: "var(--text-muted)",
      }}
      role="region"
      aria-label="Restaurant contact information"
    >
      <div
        className="flex flex-wrap items-center justify-center md:justify-between gap-x-5 gap-y-1.5 py-2"
        style={{
          paddingLeft: "clamp(1rem, 3vw, 3.5rem)",
          paddingRight: "clamp(1rem, 3vw, 3.5rem)",
        }}
      >
        {/* Phone */}
        <a
          href={phoneHref}
          className="inline-flex items-center gap-1.5 hover:text-[var(--accent)] transition-colors min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-1 rounded"
          aria-label={`Call Star Café at ${phone}`}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5 text-[var(--accent)]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
          </svg>
          <span className="font-medium">{phone}</span>
        </a>

        {/* Hours */}
        <div
          className="inline-flex items-center gap-1.5 min-h-[32px]"
          aria-label={`Opening hours: ${hours}`}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5 text-[var(--accent)]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>{hours}</span>
        </div>

        {/* Address */}
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 hover:text-[var(--accent)] transition-colors min-h-[32px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-1 rounded text-center"
          aria-label={`Open address in Google Maps: ${address}`}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5 text-[var(--accent)] shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span className="truncate max-w-[260px] sm:max-w-none">
            {address}
          </span>
        </a>
      </div>
    </div>
  );
};

export default TopInfoBar;
